import { useQuery } from "@apollo/client";
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { format, subDays, subMonths, subWeeks } from "date-fns";
import ApolloContext from "@tvg/utils/apolloContext";
import { getAccountNumber, getIsLogged } from "@urp/store-selectors";
import {
  getSelectedSettledTab,
  getSelectedTab,
  getSettledWageredAmountCounter,
  getTodaySettledCounter
} from "@tvg/sh-lib-my-bets/redux/selectors";
import {
  setSettledCounterMyBetsStandalone,
  setSettledWagerAmountMyBetsStandalone
} from "@tvg/sh-lib-my-bets/redux/actions";
import { ActiveTabEnum } from "@tvg/sh-lib-my-bets/utils/types";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect } from "react";
import { GET_SETTLED_BETS } from "../graphql/queries/SettledBets";
import type { ApolloWagerHistoryResponse } from "../types";

const useSettledBets = (customStartDate?: Date, customEndDate?: Date) => {
  const dispatch = useDispatch();

  // SELECTORS
  const accountNumber = useSelector(getAccountNumber);
  const selectedTab = useSelector(getSelectedTab);
  const isLogged = useSelector(getIsLogged);
  const selectedSettledTab = useSelector(getSelectedSettledTab);
  const settledBetsCounter = useSelector(getTodaySettledCounter);
  const settledWageredAmount = useSelector(getSettledWageredAmountCounter);

  const hasSettledTab = selectedTab.toLowerCase() === "settled";
  let startDate = new Date();
  let endDate = new Date();
  if (hasSettledTab) {
    const settledStartTimes = {
      TODAY: startDate,
      YESTERDAY: subDays(startDate, 1),
      LAST_WEEK: subWeeks(startDate, 1),
      LAST_MONTH: subMonths(startDate, 1)
    };

    startDate = customStartDate || get(settledStartTimes, selectedSettledTab);
    endDate = customEndDate || new Date();
  }

  const { behgClient } =
    useContext<{ behgClient?: ApolloClient<NormalizedCacheObject> }>(
      ApolloContext
    );
  const { loading, error, data } = useQuery<ApolloWagerHistoryResponse>(
    GET_SETTLED_BETS,
    {
      variables: {
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        accountId: parseInt(accountNumber, 10),
        status: [ActiveTabEnum.SETTLED]
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      client: behgClient,
      skip: !accountNumber || !isLogged,
      ssr: false
    }
  );

  useEffect(() => {
    if (data && !error) {
      const { wagerHistory } = data;
      if (wagerHistory?.totals) {
        dispatch(
          setSettledCounterMyBetsStandalone(wagerHistory.totals.totalCount)
        );
        dispatch(
          setSettledWagerAmountMyBetsStandalone(wagerHistory.totals.totalAmount)
        );
      }
    }
  }, [data]);

  return { loading, settledBetsCounter, settledWageredAmount };
};

export default useSettledBets;
